import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "components/fields/InputField";
import supabase from "supabaseClient";
import StorageBox from "Store/StorageBox.js";
import PersonalStorageKeys from "Utility/PersonalStorageKey.js";

export default function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // 1. Authenticate with Supabase Auth
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({ email, password });

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      // 2. Fetch the user row and check is_admin
      const { data: userRow, error: userError } = await supabase
        .from("users")
        .select("*")
        .eq("email", email)
        .single();

      if (userError || !userRow) {
        await supabase.auth.signOut();
        setError("User profile not found. Contact support.");
        setLoading(false);
        return;
      }

      if (!userRow.is_admin) {
        await supabase.auth.signOut();
        setError("You do not have admin access.");
        setLoading(false);
        return;
      }

      // 3. Store user fields in localStorage (mirrors App.tsx pattern)
      const usernameFromRow = (
        userRow.UserName ||
        userRow.user_name ||
        userRow.name ||
        ""
      ).toString();

      localStorage.setItem(
        PersonalStorageKeys.EMAIL,
        String(userRow.email || email)
      );
      localStorage.setItem(
        PersonalStorageKeys.UserEmail,
        String(userRow.email || email)
      );
      localStorage.setItem(PersonalStorageKeys.UserName, usernameFromRow);

      if (userRow.introduction) {
        localStorage.setItem(
          PersonalStorageKeys.UserIntroduction,
          String(userRow.introduction)
        );
      }
      if (userRow.country) {
        localStorage.setItem(
          PersonalStorageKeys.UserCountry,
          String(userRow.country)
        );
      }
      if (userRow.religion) {
        localStorage.setItem(
          PersonalStorageKeys.UserReligion,
          String(userRow.religion)
        );
      }
      if (userRow.quotes) {
        localStorage.setItem(
          PersonalStorageKeys.UserQuotes,
          String(userRow.quotes)
        );
      }
      if (userRow.picture_background) {
        localStorage.setItem(
          PersonalStorageKeys.PictureBackground,
          String(userRow.picture_background)
        );
      }
      if (userRow.picture_profile) {
        localStorage.setItem(
          PersonalStorageKeys.PictureProfile,
          String(userRow.picture_profile)
        );
      }
      if (userRow.picture_name) {
        localStorage.setItem(
          PersonalStorageKeys.PictureName,
          String(userRow.picture_name)
        );
      }
      if (userRow.bible_text) {
        localStorage.setItem(
          PersonalStorageKeys.BIBLEVERSE,
          String(userRow.bible_text)
        );
      }
      if (userRow.highlight) {
        localStorage.setItem(
          PersonalStorageKeys.UserHighlight,
          String(userRow.highlight)
        );
      }
      if (userRow.userdata) {
        localStorage.setItem(
          PersonalStorageKeys.UserData,
          String(userRow.userdata)
        );
      }
      if (userRow.comment) {
        localStorage.setItem(
          PersonalStorageKeys.UserComment,
          String(userRow.comment)
        );
      }

      try {
        localStorage.setItem("personal_user_row", JSON.stringify(userRow));
      } catch {}

      // 4. Redirect to admin dashboard
      navigate("/admin/default");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred."
      );
      setLoading(false);
    }
  };

  return (
    <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      {/* Sign in section */}
      <div className="mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          Admin Sign In
        </h4>
        <p className="mb-9 ml-1 text-base text-gray-600">
          Enter your email and password to access the admin panel.
        </p>

        {/* Error message */}
        {error && (
          <div className="mb-4 w-full rounded-xl border border-red-400 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full">
          {/* Email */}
          <InputField
            variant="auth"
            extra="mb-3"
            label="Email*"
            placeholder="admin@tanyapendeta.today"
            id="email"
            type="email"
            state={undefined}
            disabled={loading}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {/* Password */}
          <InputField
            variant="auth"
            extra="mb-3"
            label="Password*"
            placeholder="Min. 8 characters"
            id="password"
            type="password"
            state={undefined}
            disabled={loading}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            type="submit"
            disabled={loading}
            className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-60 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
